import type { ProductImageFraming } from '../../shared/productPresentation'
export type HomepageV3NavItem = {
  label: string
  href: string
}

export type HomepageV3Product = {
  id: string
  name: string
  type: 'Brownie Cookie Sandwich' | 'Stuffed Cookie' | 'Brownie'
  description: string
  image: string
  framing: ProductImageFraming
  availability?: 'available' | 'sold-out'
  enabled: boolean
  displayOrder: number
  isNew: boolean
  clickTarget: string
  hoverBackground: string
}

export type HomepageV3Feature = {
  id: string
  title: string
  body: string
  icon: 'oven' | 'sparkle' | 'burst' | 'fresh' | 'truck' | 'heart'
  highlighted?: boolean
}

export type HomepageV3Step = {
  number: number
  title: string
  body: string
  icon: 'menu' | 'oven' | 'truck'
}

export const homepageV3 = {
  nav: [
    { label: 'Menu', href: '/' },
    { label: 'Order Now', href: '/order' },
    { label: 'Reviews', href: '/reviews' },
    { label: "FAQ's", href: '/faq' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ] satisfies HomepageV3NavItem[],

  top: {
    dateRange: 'SEP 7 – SEP 13',
    headingLineOne: 'Cookies.',
    headingLineTwo: 'Brownies & Mischief.',
    supportingCopy: 'Brownie cookie sandwiches and stuffed cookies, baked in Christchurch for weekend delivery.',
    primaryCta: { label: 'Order Now', href: '/order' },
    secondaryCta: { label: 'View Menu', href: '#weekly-menu' },
  },

  weeklyMenu: {
    eyebrow: "THIS WEEK'S MENU",
    heading: 'Pick your favourites.',
    supportingCopy: 'Fresh weekly flavours. Mix, match and make the box yours.',
    browsePrevious: { label: 'Browse Previous Weeks', href: '/vault' },
    products: [
      {
        id: 'vanilla',
        name: 'Vanilla',
        type: 'Brownie Cookie Sandwich',
        description: 'Rich brownie cookies with creamy vanilla filling.',
        image: '/assets/homepage-v3/products/sg-menu-product-vanilla-bcs-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 10,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#F4DFC0',
      },
      {
        id: 'blueberry',
        name: 'Blueberry',
        type: 'Brownie Cookie Sandwich',
        description: 'Rich brownie cookies with real blueberry cream.',
        image: '/assets/homepage-v3/products/sg-menu-product-blueberry-bcs-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 20,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#D9D3EC',
      },
      {
        id: 'chocolate',
        name: 'Chocolate',
        type: 'Brownie Cookie Sandwich',
        description: 'Rich brownie cookies with chocolate cream.',
        image: '/assets/homepage-v3/products/sg-menu-product-chocolate-bcs-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 30,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#D4B3A2',
      },
      {
        id: 'toasted-coconut',
        name: 'Toasted Coconut',
        type: 'Brownie Cookie Sandwich',
        description: 'Rich brownie cookies with toasted coconut cream.',
        image: '/assets/homepage-v3/products/sg-menu-product-toasted-coconut-bcs-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 40,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#EBD6B9',
      },
      {
        id: 'biscoff',
        name: 'Biscoff',
        type: 'Stuffed Cookie',
        description: 'A thick cookie stuffed with Biscoff spread & cookie crumbs.',
        image: '/assets/homepage-v3/products/sg-menu-product-biscoff-sc-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 50,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#E1C29E',
      },
      {
        id: 'bueno',
        name: 'Bueno',
        type: 'Stuffed Cookie',
        description: 'A thick cookie stuffed with Bueno, chocolate & hazelnut cream.',
        image: '/assets/homepage-v3/products/sg-menu-product-bueno-sc-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 60,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#E5CBB9',
      },
      {
        id: 'banana-caramel-pb',
        name: 'Banana Caramel PB',
        type: 'Stuffed Cookie',
        description: 'A thick cookie stuffed with banana, salted caramel & peanut butter.',
        image: '/assets/homepage-v3/products/sg-menu-product-banana-caramel-pb-sc-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 70,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#F0D59E',
      },
      {
        id: 'og-chocolate',
        name: 'OG Chocolate',
        type: 'Stuffed Cookie',
        description: 'A thick cookie stuffed with melted chocolate & chocolate chunks.',
        image: '/assets/homepage-v3/products/sg-menu-product-og-choc-sc-master.png',
        framing: { focalX: 0.5, focalY: 0.5, zoom: 1 },
        enabled: true,
        displayOrder: 80,
        isNew: false,
        clickTarget: '/order',
        hoverBackground: '#CFB2A6',
      },
    ] satisfies HomepageV3Product[],
  },

  difference: {
    eyebrow: 'THE GREMLIN DIFFERENCE',
    heading: 'NOT YOUR AVERAGE TREATS',
    features: [
      { id: 'baked-properly', title: 'Baked Properly', body: 'Every batch is made to exact specifications – crispy edges, gooey centers, every single time.', icon: 'oven' },
      { id: 'premium-ingredients', title: 'Premium Ingredients', body: 'Real butter, high-quality chocolate, and carefully sourced ingredients wherever possible.', icon: 'sparkle', highlighted: true },
      { id: 'flavours-hit-different', title: 'Flavours That Hit Different', body: 'Inspired by classics from around the globe, reimagined with a Sugar Gremlin twist.', icon: 'burst' },
      { id: 'always-fresh', title: 'Always Fresh', body: 'Baked to order, never sitting on shelves. Your cookies are made when you order them.', icon: 'fresh' },
      { id: 'christchurch-delivery', title: 'Christchurch Delivery', body: 'Weekend delivery up to 35 km from our Christchurch delivery base, Saturday and Sunday from 3:00 pm to 6:00 pm.', icon: 'truck' },
      { id: 'made-with-love', title: 'Made With Love', body: 'Small-batch production from our Christchurch kitchen to your hands.', icon: 'heart' },
    ] satisfies HomepageV3Feature[],
  },

  howItWorks: {
    eyebrow: 'HOW IT WORKS',
    heading: 'Simple as 1, 2, 3.',
    steps: [
      { number: 1, title: "Browse This Week's Drop", body: 'Check out our rotating menu and choose your favourites before they’re gone.', icon: 'menu' },
      { number: 2, title: 'We Bake Fresh', body: 'Your order triggers our bakers. Every cookie is made to order, never pre-made.', icon: 'oven' },
      { number: 3, title: 'Delivered to You', body: 'We’ll deliver your cookies right to your door, up to 35 km from our Christchurch delivery base.', icon: 'truck' },
    ] satisfies HomepageV3Step[],
    cta: { label: "Order This Week's Menu", href: '/order' },
  },

  delivery: {
    eyebrow: 'FRESH TO YOUR DOOR',
    heading: 'Christchurch Delivery',
    facts: [
      { title: 'UP TO 35 KM', body: 'Delivery from our Christchurch base, with the fee calculated by distance at checkout.', icon: 'location' },
      { title: 'WEEKEND DELIVERY', body: 'Fresh bakes delivered Saturday & Sunday.', icon: 'calendar' },
      { title: 'ORDERS CLOSE FRIDAY', body: 'Get your order in by 6pm Friday.', icon: 'clock' },
    ],
    card: {
      heading: 'Christchurch Delivery',
      supportingCopy: 'Delivery up to 35 km from our Christchurch base. Fee calculated by distance.',
      pricePrefix: 'from',
      priceRange: '$4.99 – $8.99',
      priceSuffix: 'per order',
      note: 'Sweet bakes. Local love.',
    },
  },

  footer: {
    order: [
      { label: "This Week's Menu", href: '/#weekly-menu' },
      { label: 'Order Now', href: '/order' },
      { label: 'Delivery', href: '/#delivery' },
    ],
    explore: [
      { label: 'About', href: '/about' },
      { label: 'Reviews', href: '/reviews' },
      { label: 'FAQs', href: '/faq' },
      { label: 'Contact', href: '/contact' },
    ],
    legal: [
      { label: 'Terms & Conditions', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
    ],
    social: [
      { label: 'Instagram', href: 'https://www.instagram.com/sugargremlinnz/' },
      { label: 'Facebook', href: 'https://www.facebook.com/sugargremlinnz' },
    ],
    copyright: '© 2025 Sugar Gremlin. All rights reserved.',
  },
} as const

export function homepageV3EnabledProducts() {
  return homepageV3.weeklyMenu.products
    .filter((product) => product.enabled)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, 8)
}
